<template>
    <div class="addressbook-dropdown__item">
        <ui-link class="addressbook-dropdown__link"
            v-bind:to="{ name: type, params: { address: address }}"
            v-on:click="$emit('close-addressbook')"
        >
            <div class="addressbook-dropdown__item--name">
                <span v-if="name > 0" class="muted">
                    {{ $t('local_addressbook.untitled') }} {{ name }}
                </span>
                <span v-else>
                    {{ name }}
                </span>
            </div>
            <div class="muted addressbook-dropdown__item--address">
                <ui-address
                    v-bind:address="address"
                    v-bind:disabled="true"
                    v-bind:hide-name="true"
                    v-bind:hide-copy="true"
                />
            </div>
        </ui-link>
        <div class="addressbook-dropdown__controls">
            <button type="button" class="addressbook-dropdown__action"
                v-bind:class="{ 'addressbook-dropdown__action--success': successVisible }"
                v-bind:title="$t('tonconnect.copy_address')"
                v-on:click="copyAddress">
                <icon-copy v-show="!successVisible"/>
                <icon-success v-show="successVisible"/>
            </button>
            <button type="button" class="addressbook-dropdown__action"
                v-bind:title="$t('local_addressbook.title')"
                v-on:click="editItem">
                <icon-edit/>
            </button>
            <button type="button" class="addressbook-dropdown__action addressbook-dropdown__action--remove"
                v-bind:title="$t('local_addressbook.remove_address')"
                v-on:click="removeItem">
                <icon-close/>
            </button>
        </div>
    </div>
</template>

<script>
import IconCopy from '@img/icons/lucide/copy.svg?vue';
import IconClose from '@img/icons/lucide/x.svg?vue';
import IconEdit from '@img/icons/lucide/pencil.svg?vue';
import IconSuccess from '@img/icons/lucide/check.svg?vue';
import { showToast } from '~/toast';

export default {
    props: {
        address: {
            type: String,
            required: true,
        },
        name: {
            type: [String, Number],
        },
        index: {
            type: Number,
            required: true,
        },
        type: {
            type: String,
        },
    },

    data() {
        return {
            successVisible: false,
        };
    },

    methods: {
        editItem() {
            this.$emit('edit-address-name', this.index);
        },

        removeItem() {
            this.$store.dispatch('forgetAddressbookItem', { address: this.address });
        },

        async copyAddress() {
            try {
                await navigator.clipboard.writeText(this.address);
            } catch {
                // navigator.clipboard needs a secure context and a focused document;
                // otherwise copy through a throwaway field like UiCopyButton does:
                const field = document.createElement('textarea');
                field.value = this.address;
                field.setAttribute('readonly', '');
                field.style.position = 'fixed';
                field.style.opacity = '0';
                document.body.appendChild(field);
                field.select();
                document.execCommand('copy');
                field.remove();
            }
            this.successVisible = true;

            showToast(this.$t('address.info.copy_success'));

            setTimeout(() => {
                this.successVisible = false;
            }, 1800);
        },
    },

    components: {
        IconSuccess,
        IconClose,
        IconEdit,
        IconCopy,
    },
};
</script>

<style lang="scss">
.addressbook-dropdown {
    &__item {
        position: relative;
        // The list is a scrolling flex column: without this, rows squeeze
        // (clipping the address) instead of the list scrolling:
        flex-shrink: 0;
        border-radius: 9px;
        overflow: hidden;
        width: 100%;
        transition: background-color .12s ease;

        // The row is an opaque colour (not the translucent icon-button hover),
        // so the controls' gradient can fade into exactly the same tone:
        &:hover,
        &:focus-within {
            background: var(--card-pill-background);

            .addressbook-dropdown__controls {
                opacity: 1;
                pointer-events: auto;
            }
        }

        &--name {
            font-size: 14px;
            font-weight: 500;
            letter-spacing: -0.01em;
            line-height: 18px;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        &--address {
            font-size: 12px;
            line-height: 16px;
            width: 100%;
        }
    }

    &__link {
        display: flex;
        flex-direction: column;
        gap: 2px;
        padding: 8px 10px;
        white-space: nowrap;
        cursor: pointer;
        color: var(--body-text-color);
        overflow: hidden;
        text-decoration: none !important;

        &:hover {
            color: inherit;
        }
    }

    &__controls {
        position: absolute;
        display: flex;
        align-items: center;
        gap: 2px;
        right: 0;
        top: 0;
        height: 100%;
        padding: 0 6px 0 28px;
        opacity: 0;
        pointer-events: none;
        transition: opacity .12s ease;
        background: linear-gradient(to right, transparent 0%, var(--card-pill-background) 28px, var(--card-pill-background) 100%);
    }

    &__action {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        padding: 0;
        border-radius: 8px;
        color: var(--body-muted-text-color);
        cursor: pointer;
        transition: color .12s ease, background-color .12s ease, transform .1s ease;

        svg {
            display: block;
            width: 16px;
            height: 16px;
            stroke: currentColor;
            fill: none;
        }

        &:hover {
            color: var(--body-text-color);
            background: var(--icon-button-hover-background);
        }
        &:active {
            transform: scale(.92);
        }
        &:focus-visible {
            outline: 2px solid var(--blue-bright);
            outline-offset: -2px;
        }

        &--success {
            color: var(--green-bright);
        }
        &--remove:hover {
            color: var(--red-bright);
        }
    }
}
</style>
